/**
 * API Error Handler Utility
 * Handles new standardized API response formats with internationalization support
 */

import i18next from 'i18next';

// Import error messages directly as fallback
import enErrors from 'src/locales/langs/en/errors.json';

import { toast } from 'src/components/snackbar';

// Debug mode for development
const DEBUG_ERROR_HANDLING = import.meta.env.DEV; // Only in development mode

/**
 * Get localized error message by error code
 * @param {string} errorCode - Error code from API response
 * @param {string} fallbackMessage - Fallback message if translation not found
 * @returns {string} - Localized error message
 */
export const getLocalizedErrorMessage = (errorCode, fallbackMessage = 'An error occurred') => {
  if (!errorCode) {
    return fallbackMessage;
  }

  try {
    // Debug logging only in development
    if (DEBUG_ERROR_HANDLING) {
      console.log('🔍 Error code:', errorCode);
    }
    
    // First, try i18next translation
    const translatedMessage = i18next.t(errorCode, { 
      ns: 'errors',
      defaultValue: errorCode,
      fallbackLng: 'en'
    });
    
    // If translation exists and is different from the error code, return it
    if (translatedMessage && translatedMessage !== errorCode) {
      if (DEBUG_ERROR_HANDLING) {
        console.log('✅ Found i18next translation:', translatedMessage);
      }
      return translatedMessage;
    }

    // If i18next fails, try direct import as fallback
    if (enErrors[errorCode]) {
      if (DEBUG_ERROR_HANDLING) {
        console.log('✅ Using direct import fallback:', enErrors[errorCode]);
      }
      return enErrors[errorCode];
    }

    // Return error code itself if no translation found (instead of generic message)
    if (DEBUG_ERROR_HANDLING) {
      console.log('⚠️ No translation found, showing error code itself:', errorCode);
    }
    return fallbackMessage || errorCode;
  } catch (error) {
    console.warn('Translation error:', error);
    // Use direct import as last resort, show error code if no translation found
    return enErrors[errorCode] || fallbackMessage || errorCode;
  }
};

/**
 * Extract error message from different error response formats with localization
 * @param {Object} error - Error object from API response
 * @returns {string} - Formatted and localized error message
 */
export const extractErrorMessage = (error) => {
  // Check if error is a response object
  if (error?.response?.data) {
    const errorData = error.response.data;
    
    // Handle new standardized error format with error_code (priority for localization)
    if (errorData.error_code) {
      return getLocalizedErrorMessage(errorData.error_code, errorData.message);
    }
    
    // Handle 400 validation errors
    if (errorData.status === 400 && errorData.errors) {
      return formatValidationErrors(errorData.errors);
    }
    
    // Handle 500 internal server errors - check both Message and message for compatibility
    if (errorData.Message || errorData.ExceptionMessage || errorData.message) {
      return errorData.Message || errorData.message || errorData.ExceptionMessage || 'Internal server error occurred';
    }
    
    // Handle new standardized error format without error_code
    if (errorData.type && errorData.type !== 'Success') {
      return errorData.message || errorData.title || 'An error occurred';
    }
    
    // Handle old error format (backward compatibility)
    if (errorData.ErrorCode && errorData.ErrorCode !== '00') {
      return errorData.Message || errorData.message || 'An error occurred';
    }
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Handle error objects with message property
  if (error?.message) {
    return error.message;
  }
  
  return getLocalizedErrorMessage(null, 'An unexpected error occurred');
};

/**
 * Format validation errors into readable message
 * @param {Object} errors - Validation errors object
 * @returns {string} - Formatted error message
 */
export const formatValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') {
    return i18next.t('validation_error', { 
      ns: 'errors',
      defaultValue: 'Validation error occurred' 
    });
  }
  
  const errorMessages = [];
  
  Object.keys(errors).forEach(field => {
    const fieldErrors = errors[field];
    if (Array.isArray(fieldErrors)) {
      fieldErrors.forEach(errorMsg => {
        errorMessages.push(`${field}: ${errorMsg}`);
      });
    } else if (typeof fieldErrors === 'string') {
      errorMessages.push(`${field}: ${fieldErrors}`);
    }
  });
  
  return errorMessages.length > 0 
    ? errorMessages.join('; ') 
    : i18next.t('validation_error', { 
        ns: 'errors',
        defaultValue: 'Validation error occurred' 
      });
};

/**
 * Check if response indicates success
 * @param {Object} response - API response object
 * @returns {boolean} - True if response indicates success
 */
export const isSuccessResponse = (response) => {
  // Check new standardized format
  if (response?.type === 'Success' || response?.message === 'Success') {
    return true;
  }
  
  // Check old format (backward compatibility)
  if (response?.ErrorCode === '00') {
    return true;
  }
  
  // Check HTTP status codes
  if (response?.status && response.status >= 200 && response.status < 300) {
    return true;
  }
  
  return false;
};

/**
 * Extract data from response based on format
 * @param {Object} response - API response object
 * @returns {any} - Extracted data
 */
export const extractResponseData = (response) => {
  // Handle new standardized format
  if (response?.type === 'Success' && response?.data !== undefined) {
    return response.data;
  }
  
  // Handle old format (backward compatibility)
  if (response?.ErrorCode === '00' && response?.ResponseModel !== undefined) {
    return response.ResponseModel;
  }
  
  // If no specific format detected, return the response as is
  return response;
};

/**
 * Transform API response to standardized format
 * @param {Object} response - Raw API response
 * @returns {Object} - Standardized response
 */
export const transformResponse = (response) => {
  const data = response?.data || response;
  
  // If already in new format, return as is
  if (data?.type === 'Success' || data?.message === 'Success') {
    return data;
  }
  
  // Transform old format to new format
  if (data?.ErrorCode === '00') {
    return {
      message: 'Success',
      type: 'Success',
      data: data.ResponseModel,
      error_code: null
    };
  }
  
  // Return as is if format is not recognized
  return data;
};

/**
 * Handle API errors consistently with localization
 * @param {Object} error - Error object from API call
 * @param {Object} options - Options for error handling
 * @returns {Object} - Standardized error object
 */
export const handleApiError = (error, options = {}) => {
  const { showToast = true, logError = true } = options;
  
  if (logError) {
    console.error('API Error:', error);
  }
  
  const errorMessage = extractErrorMessage(error);
  
  // Show toast notification if enabled
  if (showToast) {
    toast.error(errorMessage);
  }
  
  return {
    message: errorMessage,
    originalError: error,
    isHandled: true
  };
};

/**
 * Create a standardized error response
 * @param {string} message - Error message
 * @param {string} type - Error type
 * @param {number} statusCode - HTTP status code
 * @param {string} errorCode - Error code for localization
 * @returns {Object} - Standardized error response
 */
export const createErrorResponse = (message, type = 'Error', statusCode = 500, errorCode = null) => ({
  message,
  type,
  data: null,
  error_code: errorCode,
  statusCode,
  timestamp: new Date().toISOString()
}); 